<template>
  <div class="view-container catalog-page">
    <div class="extended-filter-block">
      <h2>Каталог решений</h2>
    </div>

    <div class="card catalog-subscription">
      <div
          v-if="visibleActiveSubscriptions.length"
          class="catalog-active-subscriptions"
      >
        <h2>Активные подписки</h2>

        <div class="catalog-active-subscriptions__grid">
          <SubscriptionActiveCard
              v-for="subscription in visibleActiveSubscriptions"
              :key="subscription.title"
              :subscription="subscription"
              @order="openSubscriptionOrderForm"
          />
        </div>
      </div>

      <div v-if="availableCatalogSubscriptions.length" class="catalog-subscription__header">
        <div>
          <h2>Подписки на экосистему решений WAY2WEI</h2>
          <p>
            Подписка связывает диагностику и обучение в единый процесс. Используйте полный арсенал инструментов платформы для более глубокой работы с кадрами.
          </p>
        </div>

        <a-button
            class="secondary-button catalog-subscription__toggle"
            @click="showSubscription = !showSubscription"
        >
          <UpOutlined v-if="showSubscription" />
          <DownOutlined v-else />
        </a-button>
      </div>

      <template v-if="availableCatalogSubscriptions.length && showSubscription">
        <div
            ref="subscriptionScroller"
            class="catalog-subscription__grid"
            @mousedown="onSubscriptionMouseDown"
            @mousemove="onSubscriptionMouseMove"
            @mouseup="onSubscriptionMouseUp"
            @mouseleave="onSubscriptionMouseUp"
            @dragstart.prevent
        >
          <SubscriptionDetailsCard
              v-for="subscription in availableCatalogSubscriptions"
              :key="subscription.title"
              :subscription="subscription"
              @order="openSubscriptionOrderForm"
          />
        </div>

        <h3 class="catalog-subscription__include-title">
          Подписка включает
        </h3>

        <div class="catalog-subscription__includes">
          <a-card
              v-for="item in products.subscriptionIncludes"
              :key="item.title"
              class="catalog-include-card"
              :bordered="true"
          >
            <h4>{{ item.title }}</h4>
            <p>{{ item.description }}</p>
          </a-card>
        </div>
      </template>
    </div>

    <div
        v-for="section in productSections"
        :key="section.key"
        class="card catalog-product-section"
    >
      <div class="catalog-product-section__header">
        <h2>{{ section.title }}</h2>

        <div
            v-if="section.showQuotaSelect"
            class="catalog-product-section__quota"
        >
          <span>Сотрудников:</span>

          <a-select
              :value="selectedQuotas[section.key]"
              class="catalog-quota-select"
              dropdown-class-name="catalog-quota-dropdown"
              :dropdown-match-select-width="false"
              :bordered="false"
              @change="(value) => setSectionQuota(section.key, value)"
          >
            <a-select-option
                v-for="option in products[section.quotaOptionsKey]"
                :key="option.value"
                :value="option.value"
            >
              {{ option.label }}
            </a-select-option>
          </a-select>
        </div>
      </div>

      <div class="catalog-product-section__grid">
        <ProductDetailsCard
            v-for="product in products[section.key]"
            :key="`${product.title}-${selectedQuotas[section.key]}`"
            :product="product"
            :quota="selectedQuotas[section.key]"
            @order="(product) => openOrderForm(section.orderKey, section.key, product)"
        />
      </div>
    </div>

    <div class="card catalog-promotion">
      <div class="catalog-promotion__content">
        <h2>{{ products.promotion.title }}</h2>
        <p>{{ products.promotion.description }}</p>

        <div class="catalog-promotion__actions">
          <a-button
              class="primary-button"
              @click="openOrderForm(products.promotion.orderKey)"
          >
            {{ products.promotion.primaryButton }}
          </a-button>

          <a-button
              class="secondary-button"
              @click="openSite"
          >
            {{ products.promotion.secondaryButton }}
          </a-button>
        </div>
      </div>
    </div>

    <NewOrderForm
        v-if="showOrderForm"
        :key="`${orderFormCatalogSectionKey ?? ''}-${orderFormCatalogProduct?.orderProductKey ?? orderFormCatalogProduct?.title ?? 'order'}-${orderFormCatalogSubscription?.title ?? ''}-${orderFormCatalogQuota ?? 0}`"
        :open="showOrderForm"
        :initial-open-tables="currentOrderTables"
        :from-catalog="true"
        :initial-catalog-quota="orderFormCatalogQuota"
        :initial-catalog-quotas="selectedQuotas"
        :initial-catalog-section-key="orderFormCatalogSectionKey"
        :initial-catalog-product="orderFormCatalogProduct"
        :initial-catalog-subscription="orderFormCatalogSubscription"
        @close="closeOrderForm"
    />
  </div>
</template>

<script>
import { computed, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { DownOutlined, UpOutlined } from '@ant-design/icons-vue';

import {
  resolveActiveCatalogSubscriptions,
  resolveCatalogSectionOrderKey,
} from '@/utils/catalogPricing';
import NewOrderForm from '@/views/Orders/OrderForm.vue';
import ProductDetailsCard from '@/views/Catalog/ProductCard/ProductDetailsCard.vue';
import SubscriptionActiveCard from '@/views/Catalog/ProductCard/SubscriptionActiveCard.vue';
import SubscriptionDetailsCard from '@/views/Catalog/ProductCard/SubscriptionDetailsCard.vue';
import products from './products.json';

export default {
  name: 'Catalog',
  components: {
    DownOutlined,
    NewOrderForm,
    ProductDetailsCard,
    SubscriptionActiveCard,
    SubscriptionDetailsCard,
    UpOutlined,
  },
  setup() {
    const store = useStore();
    const showOrderForm = ref(false);
    const showSubscription = ref(true);
    const subscriptionScroller = ref(null);
    let isDraggingSubscription = false;
    let subscriptionDragStartX = 0;
    let subscriptionStartScrollLeft = 0;
    const currentOrderTables = ref([]);
    const orderFormCatalogQuota = ref(null);
    const orderFormCatalogSectionKey = ref(null);
    const orderFormCatalogProduct = ref(null);
    const orderFormCatalogSubscription = ref(null);

    const productSections = products.sections;
    const permittedSubscriptions = computed(() => store.state.subscription.permittedSubscriptions ?? []);
    const visibleActiveSubscriptions = computed(() => resolveActiveCatalogSubscriptions(
        products.activeSubscriptions,
        permittedSubscriptions.value,
    ));
    const activeCatalogSubscriptionTitles = computed(() => new Set(
        visibleActiveSubscriptions.value.map((subscription) => subscription.title),
    ));
    const availableCatalogSubscriptions = computed(() => (
        (products.subscription ?? []).filter((subscription) => (
          !activeCatalogSubscriptionTitles.value.has(subscription.title)
        ))
    ));

    const selectedQuotas = reactive({
      tests: productSections.find((section) => section.key === 'tests').defaultQuota,
      analytics: productSections.find((section) => section.key === 'analytics').defaultQuota,
      development: productSections.find((section) => section.key === 'development').defaultQuota,
      professionalDevelopment: productSections.find((section) => section.key === 'professionalDevelopment').defaultQuota,
      expertSupport: productSections.find((section) => section.key === 'expertSupport').defaultQuota,
    });

    const setSectionQuota = (sectionKey, value) => {
      selectedQuotas[sectionKey] = Number(value);
    };

    const resolveCatalogSubscriptionForOrder = (subscription) => {
      if (!subscription) {
        return null;
      }

      return products.subscription.find((item) => item.title === subscription.title) ?? subscription;
    };

    const resetCatalogOrderFormState = () => {
      orderFormCatalogQuota.value = null;
      orderFormCatalogSectionKey.value = null;
      orderFormCatalogProduct.value = null;
      orderFormCatalogSubscription.value = null;
    };

    const openOrderForm = (
        orderKey = null,
        sectionKey = null,
        catalogProduct = null,
        catalogSubscription = null,
    ) => {
      const resolvedOrderKey = orderKey || resolveCatalogSectionOrderKey(products, sectionKey);

      currentOrderTables.value = resolvedOrderKey ? [resolvedOrderKey] : [];
      orderFormCatalogSectionKey.value = sectionKey ?? null;
      orderFormCatalogQuota.value = sectionKey ? selectedQuotas[sectionKey] : null;
      orderFormCatalogProduct.value = catalogProduct ?? null;
      orderFormCatalogSubscription.value = resolveCatalogSubscriptionForOrder(catalogSubscription);

      store.dispatch('relations/clearInsufficientProducts');
      showOrderForm.value = true;
    };

    const openSubscriptionOrderForm = (subscription) => {
      openOrderForm(products.subscriptionOrderKey, null, null, subscription);
    };

    const closeOrderForm = () => {
      currentOrderTables.value = [];
      resetCatalogOrderFormState();
      showOrderForm.value = false;
    };

    const openSite = () => {
      window.open(products.promotion.link, '_blank');
    };

    const onSubscriptionMouseDown = (e) => {
      if (e.button !== 0) {
        return;
      }

      isDraggingSubscription = true;
      subscriptionDragStartX = e.clientX;
      subscriptionStartScrollLeft = subscriptionScroller.value.scrollLeft;
      e.preventDefault();
    };

    const onSubscriptionMouseMove = (e) => {
      if (!isDraggingSubscription) {
        return;
      }

      subscriptionScroller.value.scrollLeft = subscriptionStartScrollLeft - (e.clientX - subscriptionDragStartX);
      e.preventDefault();
    };

    const onSubscriptionMouseUp = () => {
      isDraggingSubscription = false;
    };

    return {
      closeOrderForm,
      availableCatalogSubscriptions,
      currentOrderTables,
      orderFormCatalogQuota,
      orderFormCatalogSectionKey,
      orderFormCatalogProduct,
      orderFormCatalogSubscription,
      openOrderForm,
      openSubscriptionOrderForm,
      openSite,
      products,
      productSections,
      selectedQuotas,
      setSectionQuota,
      showOrderForm,
      showSubscription,
      subscriptionScroller,
      visibleActiveSubscriptions,
      onSubscriptionMouseDown,
      onSubscriptionMouseMove,
      onSubscriptionMouseUp,
    };
  },
};
</script>

<style scoped lang="scss">
.catalog-page {
  display: flex;
  flex-direction: column;
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 12px;
    align-items: center;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
  font-weight: 600;
  line-height: 120%;
}

h2 {
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.catalog-subscription__header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
}

.catalog-subscription__header p {
  max-width: 800px;
  margin: 8px 0 0;
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.catalog-subscription__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
}

.catalog-subscription__grid {
  display: grid;
  grid-auto-columns: minmax(502px, 1fr);
  grid-auto-flow: column;
  gap: 16px;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 2px;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-x;
  cursor: grab;
  user-select: none;
}

.catalog-subscription__grid::-webkit-scrollbar {
  display: none;
}

.catalog-subscription__grid:active {
  cursor: grabbing;
}

.catalog-subscription__include-title {
  margin: 20px 0 12px;
  font-size: 16px;
  font-weight: 600;
  line-height: 120%;
}

.catalog-subscription__includes {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.catalog-include-card {
  height: 100%;
  max-height: 95px;
}

.catalog-include-card :deep(.ant-card-body) {
  height: 100%;
}

.catalog-include-card h4 {
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
}

.catalog-include-card p {
  margin: 0;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.catalog-active-subscriptions {
  margin-bottom: 20px;
  width: 100%;
}

.catalog-active-subscriptions__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(502px, 1fr));
  gap: 16px;
  margin-top: 12px;
}

.catalog-product-section {
  gap: 16px;
}

.catalog-product-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.catalog-product-section__quota {
  display: flex;
  align-items: center;
  gap: 8px;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.catalog-quota-select {
  width: auto;
  min-width: 0;
  color: var(--main-green);
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
}

.catalog-quota-select :deep(.ant-select-selector) {
  height: auto !important;
  padding: 0 18px 0 0 !important;
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.catalog-quota-select :deep(.ant-select-selection-search) {
  inset-inline-start: 0 !important;
}

.catalog-quota-select :deep(.ant-select-selection-search-input) {
  height: 20px !important;
}

.catalog-quota-select :deep(.ant-select-selection-item) {
  padding-right: 0 !important;
  color: var(--main-green);
  font-weight: 600;
}

.catalog-quota-select :deep(.ant-select-arrow) {
  inset-inline-end: 0;
  width: 10px;
  margin-top: -5px;
  color: var(--main-green);
}

.catalog-product-section__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
}

.catalog-promotion {
  margin-bottom: 0;
}

.catalog-promotion__content {
  max-width: 800px;
}

.catalog-promotion h2 {
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
}

.catalog-promotion p {
  font-weight: 400;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.catalog-promotion__actions {
  margin-top: 20px;
  display: flex;
  gap: 8px;
}

:global(.catalog-quota-dropdown .ant-select-item-option-selected:not(.ant-select-item-option-disabled)) {
  color: var(--main-green);
  background-color: rgba(29, 181, 19, 0.12);
}

:global(.catalog-quota-dropdown) {
  min-width: 140px !important;
}

@media (max-width: $tablet-breakpoint) {
  .catalog-subscription__grid {
    grid-auto-columns: minmax(502px, 100%);
  }

  .catalog-active-subscriptions__grid,
  .catalog-product-section__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .catalog-active-subscriptions {
    max-width: none;
  }

  .catalog-active-subscriptions__grid {
    grid-template-columns: 1fr;
  }

  .catalog-subscription__includes {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .catalog-include-card {
    max-height: none;
  }
}

@media (max-width: $mobile-breakpoint) {
  .catalog-page {
    padding: 0 12px 16px;
  }

  .catalog-subscription__header,
  .catalog-product-section__header {
    align-items: flex-start;
  }

  .catalog-subscription__grid,
  .catalog-subscription__includes,
  .catalog-active-subscriptions__grid,
  .catalog-product-section__grid {
    grid-template-columns: 1fr;
  }

  .catalog-subscription__grid {
    grid-template-columns: none;
    grid-auto-columns: 335px;
  }

  .catalog-product-section__header {
    flex-direction: column;
  }

  .catalog-promotion h2 {
    font-size: 22px;
  }

  .catalog-promotion p {
    font-size: 16px;
  }

  .catalog-promotion__actions {
    flex-direction: column;
  }
}
</style>
